/**
 * Pulse card target resolution
 *
 * Pure helpers for resolving navigable Go hrefs from pulse_cards
 * action_data / source_data / ai_analysis payloads.
 *
 * Primary responsibilities:
 * - Parse loose JSON records from pulse rows
 * - Resolve path, Space item, CRM contact/company, chat, search, prompt
 * - Cover open_chat / deep_link / open_url / start_chat / open_contact
 * - Cover open_planner / open_calendar / planner_day / calendar_context
 *
 * Dependencies:
 * - `@/lib/spaces/state` href builders (single deep-link system)
 * - `@/lib/spaces/planning-date-spans` day keys (planner `?day=` contract)
 * - Nexus CRM routes (`/nexus/crm/contacts/:id`, `/nexus/crm/companies/:id`)
 * - CRM `lead_id` / `leadId`: list deep-link `/nexus/crm?tab=contacts&leadId=`
 *   (shell + CRMNavigation `resolveInitialCrmTab` first-paint; no dashboard flash).
 *   Detail `/nexus/crm/leads/:id` still absent — `CRM_LEAD_DETAIL_ROUTE_EXISTS` stays false.
 *
 * Shared nucleus for SpacePulse and Crew Feed — do not fork href rules.
 */

import {
  getPlanningDateKey,
  parsePlanningDate
} from '@/lib/spaces/planning-date-spans'
import {
  buildSpaceItemHref,
  buildStandaloneSpaceItemHref
} from '@/lib/spaces/state'
import type { SpaceLinkedItemType } from '@/types/spaces'

/** Re-export Space-item builders — Crew Go nucleus (same as mobile thin copies). */
export { buildSpaceItemHref, buildStandaloneSpaceItemHref }
export type { SpaceLinkedItemType }

/** Loose JSON object drawn from pulse_cards jsonb columns. */
export type PulseJsonRecord = Record<string, unknown>

export const SPACE_ITEM_TYPES: readonly SpaceLinkedItemType[] = [
  'task',
  'sheet',
  'note',
  'sprint',
  'folder',
  'conversation',
  'design',
  'whiteboard'
] as const

// --------------

/**
 * Chat `?query=` encoding — spaces as `+` (matches discuss / discussHref callers).
 */
export function encodeChatQuery(value: string): string {
  return encodeURIComponent(value).replace(/%20/g, '+')
}

/** Build `/chat?query=…` from a prompt seed. */
export function buildChatQueryHref(prompt: string): string {
  return `/chat?query=${encodeChatQuery(prompt)}`
}

/**
 * Nexus CRM contact detail — matches leads-list /
 * `app/(private)/nexus/crm/contacts/[contactId]` routes.
 * Optional workspaceId mirrors `?workspaceId=` used across Nexus CRM.
 */
export function buildCrmContactHref(
  contactId: string,
  workspaceId?: string | null
): string {
  const base = `/nexus/crm/contacts/${contactId.trim()}`
  const ws = typeof workspaceId === 'string' ? workspaceId.trim() : ''
  if (!ws) return base
  return `${base}?workspaceId=${encodeURIComponent(ws)}`
}

/**
 * Nexus CRM company detail — matches
 * `app/(private)/nexus/crm/companies/[companyId]` routes.
 */
export function buildCrmCompanyHref(
  companyId: string,
  workspaceId?: string | null
): string {
  const base = `/nexus/crm/companies/${companyId.trim()}`
  const ws = typeof workspaceId === 'string' ? workspaceId.trim() : ''
  if (!ws) return base
  return `${base}?workspaceId=${encodeURIComponent(ws)}`
}

/**
 * CRM lead list deep-link — unified CRM contacts tab with `leadId` query.
 * Audited: contacts legacy page + nexus-crm-shell / crm-navigation consume
 * `leadId` (split-pane / initialLeadId). This is NOT a lead detail route —
 * `CRM_LEAD_DETAIL_ROUTE_EXISTS` stays false (no `/nexus/crm/leads/:id`).
 */
export function buildCrmLeadHref(
  leadId: string,
  workspaceId?: string | null
): string {
  const id = leadId.trim()
  const base = `/nexus/crm?tab=contacts&leadId=${encodeURIComponent(id)}`
  const ws = typeof workspaceId === 'string' ? workspaceId.trim() : ''
  if (!ws) return base
  return `${base}&workspaceId=${encodeURIComponent(ws)}`
}

/**
 * Refuse / rewrite invented `/nexus/crm/leads/:id` when detail route is
 * absent (`CRM_LEAD_DETAIL_ROUTE_EXISTS === false`). Prefer list deep-link
 * via `buildCrmLeadHref` when an id is extractable; otherwise null.
 * Non-lead paths pass through unchanged.
 */
export function rewriteAbsentCrmLeadDetailHref(
  path: string,
  workspaceId?: string | null
): string | null {
  const trimmed = typeof path === 'string' ? path.trim() : ''
  if (!trimmed) return null
  if (CRM_LEAD_DETAIL_ROUTE_EXISTS) return trimmed

  // Bare /leads or /leads/ with no id — refuse (no detail route).
  if (/^\/nexus\/crm\/leads\/?$/i.test(trimmed.split(/[?#]/)[0] || '')) {
    return null
  }

  const match = trimmed.match(/^\/nexus\/crm\/leads\/([^/?#]+)(.*)$/i)
  if (!match) return trimmed

  const id = (() => {
    try {
      return decodeURIComponent(match[1] || '').trim()
    } catch {
      return (match[1] || '').trim()
    }
  })()
  if (!id) return null

  const suffix = match[2] || ''
  let ws =
    typeof workspaceId === 'string' && workspaceId.trim()
      ? workspaceId.trim()
      : ''
  if (!ws && suffix.startsWith('?')) {
    try {
      const params = new URLSearchParams(suffix.slice(1))
      ws =
        params.get('workspaceId')?.trim() ||
        params.get('workspace_id')?.trim() ||
        ''
    } catch {
      ws = ''
    }
  }
  return buildCrmLeadHref(id, ws || null)
}

/** Canonical Spaces planner surface (overview / day deep-links). */
export const PLANNER_HREF = '/spaces/planner'

/**
 * Spaces planner day deep-link — `/spaces/planner` or
 * `/spaces/planner?day=yyyy-MM-dd` using planning-date-spans keys
 * (same day contract as Pulse planner data).
 */
export function buildPlannerDayHref(day?: string | null): string | null {
  const raw = typeof day === 'string' ? day.trim() : ''
  if (!raw) return null

  const parsed = parsePlanningDate(raw)
  if (!parsed) return null

  return `${PLANNER_HREF}?day=${encodeURIComponent(getPlanningDateKey(parsed))}`
}

// --------------

/**
 * Narrow unknown / Json jsonb into a plain object record.
 */
export function getJsonRecord(value: unknown): PulseJsonRecord | null {
  if (!value || Array.isArray(value) || typeof value !== 'object') {
    return null
  }

  return value as PulseJsonRecord
}

// --------------

/**
 * First non-empty trimmed string among the given keys.
 */
export function getRecordString(
  record: PulseJsonRecord | null,
  ...keys: string[]
): string | null {
  if (!record) {
    return null
  }

  for (const key of keys) {
    const candidate = record[key]

    if (typeof candidate === 'string' && candidate.trim().length > 0) {
      return candidate.trim()
    }
  }

  return null
}

// --------------

function firstRecordString(
  records: Array<PulseJsonRecord | null>,
  ...keys: string[]
): string | null {
  for (const record of records) {
    const value = getRecordString(record, ...keys)
    if (value) return value
  }
  return null
}

/**
 * Generic CRM open — may carry contact OR company id.
 * Kept separate from contact-only aliases so company_id is not dropped
 * onto the People tab (dead Go).
 */
function isCrmGenericAction(actionType: string | null): boolean {
  return actionType === 'open_crm' || actionType === 'openCrm'
}

function isCrmContactAction(actionType: string | null): boolean {
  return (
    actionType === 'open_contact' ||
    actionType === 'openContact' ||
    actionType === 'open_crm_contact' ||
    actionType === 'openCrmContact' ||
    actionType === 'view_contact' ||
    actionType === 'viewContact'
  )
}

function isCrmCompanyAction(actionType: string | null): boolean {
  return (
    actionType === 'open_company' ||
    actionType === 'openCompany' ||
    actionType === 'open_crm_company' ||
    actionType === 'openCrmCompany' ||
    actionType === 'view_company' ||
    actionType === 'viewCompany'
  )
}

function isCrmLeadAction(actionType: string | null): boolean {
  return (
    actionType === 'open_lead' ||
    actionType === 'openLead' ||
    actionType === 'open_crm_lead' ||
    actionType === 'openCrmLead' ||
    actionType === 'view_lead' ||
    actionType === 'viewLead'
  )
}

/** Coerce pulse CRM ids (string or finite number) to a non-empty trimmed string. */
function coerceCrmId(value: unknown): string | null {
  if (typeof value === 'string' && value.trim().length > 0) {
    return value.trim()
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value)
  }
  return null
}

const CRM_CONTACT_ID_KEYS = [
  'contact_id',
  'contactId',
  'crm_contact_id',
  'crmContactId',
  'person_id',
  'personId'
] as const

const CRM_COMPANY_ID_KEYS = [
  'company_id',
  'companyId',
  'crm_company_id',
  'crmCompanyId',
  'account_id',
  'accountId'
] as const

/**
 * Lead list deep-link is live (`?leadId=` on contacts tab). Lead *detail*
 * `/nexus/crm/leads/:id` still does NOT exist — keep this false so callers
 * never invent a detail href. Prefer `buildCrmLeadHref` (list deep-link).
 *
 * TODO(crew-crm-lead-go): flip when a real lead detail route ships, then
 * prefer detail over list deep-link.
 */
export const CRM_LEAD_DETAIL_ROUTE_EXISTS = false as const

/**
 * Unified CRM contacts tab accepts `leadId` query (shell + contacts legacy
 * redirect + CRMNavigation resolveInitialCrmTab first-paint). Honest Go
 * target for lead payloads without a detail page.
 */
export const CRM_LEAD_LIST_DEEPLINK_EXISTS = true as const

/** Lead id payload keys — used by `firstCrmLeadId` / `buildCrmLeadHref`. */
export const CRM_LEAD_ID_KEYS = [
  'lead_id',
  'leadId',
  'crm_lead_id',
  'crmLeadId'
] as const

const CRM_CONTACT_NEST_KEYS = [
  'contact',
  'target',
  'payload',
  'entity'
] as const

const CRM_COMPANY_NEST_KEYS = [
  'company',
  'account',
  'target',
  'payload',
  'entity'
] as const

const CRM_NEST_CONTACT_ID_KEYS = ['id', 'contact_id', 'contactId'] as const

const CRM_NEST_COMPANY_ID_KEYS = ['id', 'company_id', 'companyId'] as const

function getCrmIdFromRecord(
  record: PulseJsonRecord | null,
  keys: readonly string[],
  nestKeys: readonly string[],
  nestIdKeys: readonly string[],
  allowGenericId: boolean
): string | null {
  if (!record) return null

  for (const key of keys) {
    const id = coerceCrmId(record[key])
    if (id) return id
  }

  if (allowGenericId) {
    const id = coerceCrmId(record.id)
    if (id) return id
  }

  for (const nest of nestKeys) {
    const nested = getJsonRecord(record[nest])
    if (!nested) continue
    for (const key of nestIdKeys) {
      const id = coerceCrmId(nested[key])
      if (id) return id
    }
  }

  return null
}

/**
 * First resolvable CRM contact id across action/source/ai records.
 * Flat keys + nested contact/target/payload/entity + entity_type=contact.
 */
function firstCrmContactId(
  records: Array<PulseJsonRecord | null>,
  allowGenericId: boolean
): string | null {
  for (const record of records) {
    const id = getCrmIdFromRecord(
      record,
      CRM_CONTACT_ID_KEYS,
      CRM_CONTACT_NEST_KEYS,
      CRM_NEST_CONTACT_ID_KEYS,
      allowGenericId
    )
    if (id) return id
  }

  for (const record of records) {
    if (!record) continue
    const entityType = getRecordString(record, 'entity_type', 'entityType')
    if (entityType !== 'contact' && entityType !== 'person') continue
    const id =
      coerceCrmId(record.entity_id) ||
      coerceCrmId(record.entityId) ||
      coerceCrmId(record.id)
    if (id) return id
  }

  return null
}

/**
 * First resolvable CRM company id across action/source/ai records.
 * Flat keys + nested company/account/target/payload/entity + entity_type=company.
 */
function firstCrmCompanyId(
  records: Array<PulseJsonRecord | null>,
  allowGenericId: boolean
): string | null {
  for (const record of records) {
    const id = getCrmIdFromRecord(
      record,
      CRM_COMPANY_ID_KEYS,
      CRM_COMPANY_NEST_KEYS,
      CRM_NEST_COMPANY_ID_KEYS,
      allowGenericId
    )
    if (id) return id
  }

  for (const record of records) {
    if (!record) continue
    const entityType = getRecordString(record, 'entity_type', 'entityType')
    if (entityType !== 'company' && entityType !== 'account') continue
    const id =
      coerceCrmId(record.entity_id) ||
      coerceCrmId(record.entityId) ||
      coerceCrmId(record.id)
    if (id) return id
  }

  return null
}

/**
 * First resolvable CRM lead id across action/source/ai records.
 * Flat CRM_LEAD_ID_KEYS + entity_type=lead. Never invents `/leads/:id`.
 */
function firstCrmLeadId(
  records: Array<PulseJsonRecord | null>,
  allowGenericId: boolean
): string | null {
  for (const record of records) {
    if (!record) continue
    for (const key of CRM_LEAD_ID_KEYS) {
      const id = coerceCrmId(record[key])
      if (id) return id
    }
    if (allowGenericId) {
      const id = coerceCrmId(record.id)
      if (id) return id
    }
  }

  for (const record of records) {
    if (!record) continue
    const entityType = getRecordString(record, 'entity_type', 'entityType')
    if (entityType !== 'lead') continue
    const id =
      coerceCrmId(record.entity_id) ||
      coerceCrmId(record.entityId) ||
      coerceCrmId(record.id)
    if (id) return id
  }

  return null
}

/** People-tab CRM shell — retained for lockstep / intentional tab deep-links.
 * Bare open_crm / open_contact no longer call this (fail-closed anti-slop). */
function buildCrmContactsTabHref(workspaceId: string | null): string {
  return workspaceId
    ? `/nexus/crm?tab=contacts&workspaceId=${encodeURIComponent(workspaceId)}`
    : '/nexus/crm?tab=contacts'
}

/** Companies-tab CRM shell — retained for lockstep / intentional tab deep-links.
 * Bare open_company no longer calls this (fail-closed anti-slop). */
function buildCrmCompaniesTabHref(workspaceId: string | null): string {
  return workspaceId
    ? `/nexus/crm?tab=companies&workspaceId=${encodeURIComponent(workspaceId)}`
    : '/nexus/crm?tab=companies'
}

function isPlannerAction(actionType: string | null): boolean {
  return (
    actionType === 'open_planner' ||
    actionType === 'openPlanner' ||
    actionType === 'open_calendar' ||
    actionType === 'openCalendar' ||
    actionType === 'planner_day' ||
    actionType === 'plannerDay' ||
    actionType === 'calendar_context' ||
    actionType === 'calendarContext'
  )
}

/** Day payload keys used once action type is known to be planner. */
const PLANNER_DAY_KEYS_TYPED = [
  'day',
  'date',
  'date_key',
  'dateKey',
  'planner_day',
  'plannerDay',
  'start_date',
  'startDate',
  'event_date',
  'eventDate'
] as const

/**
 * Untyped planner-day keys — avoid generic `date` so other cards
 * with timestamps do not hijack Go to planner.
 */
const PLANNER_DAY_KEYS_UNTYPED = [
  'day',
  'date_key',
  'dateKey',
  'planner_day',
  'plannerDay'
] as const

function getPlannerDayValue(
  records: Array<PulseJsonRecord | null>,
  typed: boolean
): string | null {
  const keys = typed ? PLANNER_DAY_KEYS_TYPED : PLANNER_DAY_KEYS_UNTYPED
  return firstRecordString(records, ...keys)
}

/**
 * Whether a `/chat` href is a live Go / Discuss target — conversation
 * thread (`/chat/<id>…`) or non-empty `?query=` seed. Bare `/chat`,
 * `/chat/`, empty `?query=`, or non-chat paths are dead.
 * Shared by getPulseTarget early path + feed-card Discuss/hasGo/goHref.
 * Keep identical in mobile/lib/crew/pulse-go.ts.
 */
export function isLivePulseChatHref(href: string): boolean {
  const trimmed = typeof href === 'string' ? href.trim() : ''
  if (!trimmed.startsWith('/chat')) return false
  const pathOnly = trimmed.split('?')[0] ?? ''
  // Thread path — not bare `/chat` or `/chat/`.
  if (/^\/chat\/.+/u.test(pathOnly)) return true
  const match = trimmed.match(/[?&]query=([^&]*)/)
  if (!match) return false
  try {
    return decodeURIComponent(match[1].replace(/\+/g, ' ')).trim().length > 0
  } catch {
    return match[1].trim().length > 0
  }
}

/**
 * Resolve a navigable href from pulse action/source/ai payloads.
 * Prefers explicit path/href/url, then typed actions, Space items,
 * CRM contact/company, chat, search, prompt, space.
 *
 * Covered action types: open_chat, deep_link, open_url, start_chat,
 * open_contact / open_crm / view_contact, open_company / open_crm_company,
 * open_planner / open_calendar / planner_day / calendar_context,
 * search_related / search (plus untyped path/chat_id/contact_id/
 * company_id/crm_*_id/entity_type/day/query/prompt payloads).
 * Bare deep_link / open_url / search / start_chat / planner / CRM without
 * a live payload fail-closed (no fall-through invent).
 * Generic open_crm resolves contact first, then company (never drops
 * company_id onto the People tab).
 */
export function getPulseTarget(
  actionData: PulseJsonRecord | null,
  sourceData: PulseJsonRecord | null,
  aiAnalysis: PulseJsonRecord | null = null
): string | null {
  const records = [actionData, sourceData, aiAnalysis]

  // Explicit navigable string — deep_link / open_url / raw path
  const path =
    getRecordString(actionData, 'path', 'href', 'url') ||
    getRecordString(sourceData, 'path', 'href', 'url')

  if (path) {
    // Dead bare `/chat` — refuse (same live gate as Discuss / hasGo).
    // Insight/memory repair stays null (no Discuss-as-Go invent).
    if (path.startsWith('/chat') && !isLivePulseChatHref(path)) {
      return null
    }
    // Invented `/nexus/crm/leads/:id` → list deep-link or refuse (no detail).
    return rewriteAbsentCrmLeadDetailHref(path)
  }

  const actionType =
    getRecordString(actionData, 'type', 'action') ||
    getRecordString(sourceData, 'type', 'action')

  // deep_link / open_url — path/href/url already gated above. Bare type is
  // dead Go (anti-slop; do not fall through to chat_id/CRM/space invent).
  // Prefer typed open_chat / open_contact / item_type+item_id for those targets.
  if (
    actionType === 'deep_link' ||
    actionType === 'deepLink' ||
    actionType === 'open_url' ||
    actionType === 'openUrl'
  ) {
    return null
  }

  // start_chat — prompt/query/message → seeded chat; bare type is dead Go
  // (Discuss already treats bare `/chat` as dead — do not mount empty shell).
  if (actionType === 'start_chat' || actionType === 'startChat') {
    const prompt =
      getRecordString(actionData, 'prompt', 'query', 'message') ||
      getRecordString(sourceData, 'prompt', 'query', 'message')
    if (prompt) {
      return buildChatQueryHref(prompt)
    }
    return null
  }

  // search_related — q alias + prompt fallback. Bare type is dead Go
  // (anti-slop; same class as bare start_chat — do not fall through).
  if (
    actionType === 'search_related' ||
    actionType === 'search' ||
    actionType === 'searchRelated'
  ) {
    const query =
      getRecordString(actionData, 'query', 'q', 'prompt') ||
      getRecordString(sourceData, 'query', 'q', 'prompt')
    if (query) {
      return `/s?q=${encodeURIComponent(query)}`
    }
    return null
  }

  // open_planner / open_calendar / planner_day / calendar_context
  // Fail-closed without a parseable day — do not dump bare /spaces/planner
  // (anti-slop; same class as bare start_chat).
  if (isPlannerAction(actionType)) {
    const day = getPlannerDayValue(records, true)
    if (day && parsePlanningDate(day)) {
      return buildPlannerDayHref(day)
    }
    return null
  }

  // open_url with only a nested payload-style key already covered by url above.

  // open_chat — only chat_id (path already gated). Do not fall through to
  // Space-item / CRM invent (anti-slop; same class as bare search / deep_link).
  if (actionType === 'open_chat' || actionType === 'openChat') {
    const chatId =
      getRecordString(actionData, 'chat_id', 'chatId') ||
      getRecordString(sourceData, 'chat_id', 'chatId') ||
      getRecordString(aiAnalysis, 'chat_id', 'chatId')
    if (chatId) {
      return `/chat/${chatId}`
    }
    return null
  }

  const spaceId =
    getRecordString(actionData, 'space_id', 'spaceId') ||
    getRecordString(sourceData, 'space_id', 'spaceId')

  const itemType =
    getRecordString(actionData, 'item_type', 'itemType') ||
    getRecordString(sourceData, 'item_type', 'itemType')
  const itemId =
    getRecordString(actionData, 'item_id', 'itemId') ||
    getRecordString(sourceData, 'item_id', 'itemId')

  if (
    itemType &&
    itemId &&
    SPACE_ITEM_TYPES.includes(itemType as SpaceLinkedItemType)
  ) {
    return spaceId
      ? buildSpaceItemHref(spaceId, {
          type: itemType as SpaceLinkedItemType,
          id: itemId
        })
      : buildStandaloneSpaceItemHref({
          type: itemType as SpaceLinkedItemType,
          id: itemId
        })
  }

  for (const type of SPACE_ITEM_TYPES) {
    const targetId = records.reduce<string | null>(
      (resolved, record) =>
        resolved || getRecordString(record, `${type}_id`, `${type}Id`),
      null
    )

    if (targetId) {
      return spaceId
        ? buildSpaceItemHref(spaceId, {
            type,
            id: targetId
          })
        : buildStandaloneSpaceItemHref({
            type,
            id: targetId
          })
    }
  }

  const workspaceId = firstRecordString(records, 'workspace_id', 'workspaceId')

  // Typed CRM — generic open_crm may carry contact OR company (F3 dead-Go fix)
  // or lead via contacts-tab ?leadId= list deep-link (not /leads/:id).
  if (isCrmGenericAction(actionType)) {
    const contactId = firstCrmContactId(records, true)
    if (contactId) {
      return buildCrmContactHref(contactId, workspaceId)
    }
    const companyId = firstCrmCompanyId(records, true)
    if (companyId) {
      return buildCrmCompanyHref(companyId, workspaceId)
    }
    const leadId = firstCrmLeadId(records, true)
    if (leadId && CRM_LEAD_LIST_DEEPLINK_EXISTS) {
      return buildCrmLeadHref(leadId, workspaceId)
    }
    // Bare open_crm — fail-closed (anti-slop). Do not dump People tab.
    return null
  }

  if (isCrmContactAction(actionType)) {
    const contactId = firstCrmContactId(records, true)
    if (contactId) {
      return buildCrmContactHref(contactId, workspaceId)
    }
    // Bare open_contact — fail-closed (anti-slop). Do not dump People tab.
    return null
  }

  if (isCrmCompanyAction(actionType)) {
    const companyId = firstCrmCompanyId(records, true)
    if (companyId) {
      return buildCrmCompanyHref(companyId, workspaceId)
    }
    // Bare open_company — fail-closed (anti-slop). Do not dump Companies tab.
    return null
  }

  if (isCrmLeadAction(actionType)) {
    const leadId = firstCrmLeadId(records, true)
    if (leadId && CRM_LEAD_LIST_DEEPLINK_EXISTS) {
      return buildCrmLeadHref(leadId, workspaceId)
    }
    // Bare open_lead — fail-closed (anti-slop). Do not dump People tab.
    return null
  }

  // Untyped CRM ids — prefer contact over company over lead
  {
    const contactId = firstCrmContactId(records, false)
    if (contactId) {
      return buildCrmContactHref(contactId, workspaceId)
    }

    const companyId = firstCrmCompanyId(records, false)
    if (companyId) {
      return buildCrmCompanyHref(companyId, workspaceId)
    }

    const leadId = firstCrmLeadId(records, false)
    if (leadId && CRM_LEAD_LIST_DEEPLINK_EXISTS) {
      return buildCrmLeadHref(leadId, workspaceId)
    }
  }

  const chatId =
    getRecordString(actionData, 'chat_id', 'chatId') ||
    getRecordString(sourceData, 'chat_id', 'chatId') ||
    getRecordString(aiAnalysis, 'chat_id', 'chatId')

  if (chatId) {
    return `/chat/${chatId}`
  }

  // Untyped day / date_key / planner_day alone is not a Go seed (anti-slop).
  // Same class as untyped query / bare spaceId / bare CRM — fail-closed.
  // Only typed calendar / calendar_context / open_planner may invent planner day.
  void getPlannerDayValue(records, false)

  // Untyped prompt/message alone is not a Go seed (anti-slop). Only typed
  // start_chat / startChat may invent /chat?query= (gated above).

  // Bare spaceId without item_type+item_id — fail-closed (anti-slop).
  // Do not dump `/spaces/:id` shell (same class as bare CRM / planner / start_chat).
  void spaceId

  return null
}

/**
 * Alias — Crew Feed / SpacePulse should call the same nucleus.
 * Prefer getPulseTarget; this name is kept for existing call sites/tests.
 */
export const resolvePulseGoHref = getPulseTarget
